import { Link, useParams } from "react-router-dom";
import { supplierEvalApi } from "../../api";
import { ProcError, ProcField, ProcForm, ProcGrid, ProcLoading, ProcSection } from "../../components/ProcurementLayout";
import { ProcStatusBadge } from "../../components/ProcStatusBadge";
import { useAsyncData } from "../../hooks";

const CRITERIA = [
  { key: "deliversOnTime" as const, label: "1. Delivers on Time" },
  { key: "conditionOnArrival" as const, label: "2. Condition on Arrival" },
  { key: "followsInstructions" as const, label: "3. Follows Instructions" },
  { key: "numberOfRejections" as const, label: "4. Number of Rejections" },
  { key: "handlesRejections" as const, label: "5. Handles Rejections" },
  { key: "handlingComplaints" as const, label: "6. Handling of Complaints" },
  { key: "technicalAssistance" as const, label: "7. Technical Assistance" },
  { key: "emergencyAid" as const, label: "8. Emergency Aid" },
  { key: "furnishesInfo" as const, label: "9. Furnishes Information" },
  { key: "deliversWithoutFollowup" as const, label: "10. Delivers Without Follow-up" },
  { key: "reliabilityRecord" as const, label: "11. Reliability Record" },
  { key: "technicalAbility" as const, label: "12. Technical Ability" },
  { key: "overallRatingAgency" as const, label: "13. Overall Rating (Agency)" },
  { key: "overallRatingPurchasing" as const, label: "14. Overall Rating (Purchasing)" },
];

const RATING_LABELS: Record<number, string> = { 1: "Poor", 2: "Fair", 3: "Good", 4: "Excellent" };

export default function SupplierEvalViewPage() {
  const { id } = useParams<{ id: string }>();

  const { data: ev, loading, error } = useAsyncData(
    () => supplierEvalApi.get(Number(id)),
    [id],
  );

  if (loading) return <ProcLoading />;
  if (error) return <ProcError message={error} />;
  if (!ev) return null;

  const computedTotal = CRITERIA.reduce((s, { key }) => s + ev[key], 0);
  const percentage = Math.round((computedTotal / 56) * 100);
  const scoreColor = percentage >= 80 ? "text-emerald-700" : percentage >= 60 ? "text-amber-600" : "text-red-600";

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/procurement/supplier-eval" className="text-sm text-emerald-700 hover:underline">&larr; Back to Supplier Evaluations</Link>
      </div>

      <ProcForm formNo="PROC-FRM-08" title="Supplier Performance Evaluation" serialNo={ev.serialNo} date={ev.date}>
        <ProcSection title="Details">
          <ProcGrid>
            <ProcField label="Supplier" value={ev.supplierName} />
            <ProcField label="Evaluation Period" value={ev.evaluationPeriod} />
            <ProcField label="Recommendation" value={<ProcStatusBadge value={ev.recommendation} />} />
          </ProcGrid>
        </ProcSection>

        <ProcSection title="Performance Ratings">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Criterion</th>
                <th className="px-3 py-2 text-right">Score</th>
                <th className="px-3 py-2 text-left">Rating</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {CRITERIA.map(({ key, label }) => (
                <tr key={key}>
                  <td className="px-3 py-2 text-slate-700">{label}</td>
                  <td className="px-3 py-2 text-right font-semibold">{ev[key]}</td>
                  <td className="px-3 py-2 text-slate-500">{RATING_LABELS[ev[key]] ?? "—"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 font-bold">
                <td className="px-3 py-2">Total</td>
                <td className={`px-3 py-2 text-right ${scoreColor}`}>{computedTotal} / 56</td>
                <td className={`px-3 py-2 ${scoreColor}`}>{percentage}%</td>
              </tr>
            </tfoot>
          </table>
        </ProcSection>

        {ev.comments && (
          <ProcSection title="Comments">
            <p className="text-sm text-slate-700">{ev.comments}</p>
          </ProcSection>
        )}
      </ProcForm>
    </div>
  );
}
